import { requestWithNewUrl } from '../../../request';
import { getOriginFromReq } from '../../getOriginFromReq';
import { isServer } from '../../utils';

function safeParse<T extends Record<string, unknown>>(str: string): T | undefined {
  try {
    return JSON.parse(str);
  } catch {
    return undefined;
  }
}

// k8s injects service env vars as UPPER_SNAKE, so a tier name like `us-east` becomes `US_EAST`
const tierName = (process.env.TIER_NAME || 'global').toUpperCase().replace(/-/g, '_');
// to override env mapping eg {API: "OPS_RUNTIME_GLOBAL_SVC_SERVICE_HOST"}, will use process.env.OPS_RUNTIME_GLOBAL_SVC_SERVICE_HOST for API
const INTERNAL_SVC_HOST_MAP =
  typeof process.env.INTERNAL_SVC_HOST_MAP === 'string'
    ? safeParse<Record<string, string>>(process.env.INTERNAL_SVC_HOST_MAP)
    : undefined;
const INTERNAL_SVC_PORT_MAP =
  typeof process.env.INTERNAL_SVC_PORT_MAP === 'string'
    ? safeParse<Record<string, string>>(process.env.INTERNAL_SVC_PORT_MAP)
    : undefined;

const getEnv = (svc: string, type: string, envMap: Record<string, string> | undefined) => {
  if (envMap && typeof envMap === 'object' && typeof envMap[svc] === 'string' && envMap[svc]) {
    // if env var is present, use that
    if (process.env[envMap[svc]]) return process.env[envMap[svc]];
    // else use the mapping value directly, could be a direct value
    return envMap[svc];
  }

  return (
    // tier name might be set but svc is not there, so we need to check for global as well
    process.env[`${svc}_${tierName}_SVC_SERVICE_${type}`] ??
    process.env[`${svc}_GLOBAL_SVC_SERVICE_${type}`] ??
    ''
  );
};

const INTERNAL_SVC_HOST = {
  '/api/': getEnv('API', 'HOST', INTERNAL_SVC_HOST_MAP),
  '/auth/': getEnv('AUTH', 'HOST', INTERNAL_SVC_HOST_MAP),
  '/api-endpoint/': getEnv('API_PLATFORM', 'HOST', INTERNAL_SVC_HOST_MAP),
};

const INTERNAL_SVC_PORT = {
  '/api/': getEnv('API', 'PORT_EXTERNAL', INTERNAL_SVC_PORT_MAP),
  '/auth/': getEnv('AUTH', 'PORT_EXTERNAL', INTERNAL_SVC_PORT_MAP),
  '/api-endpoint/': getEnv('API_PLATFORM', 'PORT_EXTERNAL', INTERNAL_SVC_PORT_MAP),
};

// log if any override is present
if (process.env.INTERNAL_SVC_HOST_MAP || process.env.INTERNAL_SVC_PORT_MAP) {
  console.debug('internal routing maps', {
    INTERNAL_SVC_HOST_MAP,
    INTERNAL_SVC_PORT_MAP,
    INTERNAL_SVC_HOST,
    INTERNAL_SVC_PORT,
  });
}
const DISABLE_INTERNAL_SVC_ROUTING =
  String(process.env.DISABLE_INTERNAL_SVC_ROUTING).toLowerCase() === 'true';

export const getInternalSvcRequest = async (request: Request) => {
  if (isServer() && !DISABLE_INTERNAL_SVC_ROUTING) {
    // req from nextjs server req
    const origin = await getOriginFromReq();
    // url to fetch
    const reqUrl = new URL(request.url);
    let pathname = reqUrl.pathname;
    const host = reqUrl.host;
    // Check if the request is for microservices
    if (reqUrl.origin === origin) {
      const { headers: nextHeaders } = await import('next/headers');
      const header = await nextHeaders();

      // for path based domain BE will proxy to FE with this header, we can remove it from path and directly forward to internal svc, passing this header
      const basePathHeader = header.get('x-ua-hosted-path') ?? '';
      if (basePathHeader) {
        pathname = pathname.replace(basePathHeader, '');
        request.headers.set('x-ua-hosted-path', basePathHeader);
      }

      for (const key in INTERNAL_SVC_HOST) {
        // Check if the request is for internal service, and it's env is present
        if (pathname.startsWith(key) && INTERNAL_SVC_HOST[key] && INTERNAL_SVC_PORT[key]) {
          const internalHost = `http://${INTERNAL_SVC_HOST[key]}:${INTERNAL_SVC_PORT[key]}`;
          // Replace the origin with internal service host
          let internalUrl = request.url.replace(origin, internalHost);
          if (basePathHeader) {
            internalUrl = internalUrl.replace(basePathHeader, '');
          }
          request.headers.set('host', host);
          // x-forwarded-host is preferred over host header and so setting it. for this it is not overridden by proxy
          request.headers.set('X-Forwarded-Host', host);
          return requestWithNewUrl(request, internalUrl);
        }
      }
    }
  }
};
